import memeService from "./../MemeService";
import notificationService from "./../NotificationService";
import { useMemo, useCallback } from "react";
import useEntityState from "./../entityTemplateReducer";
import { useTranslation } from "react-i18next";

const PreloadModerationService = () => {
    const { t } = useTranslation("preloadErrors");

    const {
        getEntities,
        deleteEntityState,
        setEntityState,
        updateEntityState,
        addEntityState,
        addFirstEntityState,
    } = useEntityState();

    const moderators = useMemo(() => {
        return getEntities("moderators");
    }, [getEntities]);

    const setModerator = useCallback(
        (moderator) => {
            if (!moderator)
                return console.warn(
                    "PreloadModerationService: setModerator missing moderator",
                );

            // Do not use this to update 1 template
            setEntityState(moderator, "moderators");
        },
        [setEntityState],
    );

    const replaceModerator = useCallback(
        (moderator) => {
            if (!moderator)
                return console.warn(
                    "PreloadModerationService: replaceModerator missing moderator",
                );
            updateEntityState(moderator, "moderators");
        },
        [updateEntityState],
    );

    const updateModerator = useCallback(
        async (moderator, premission) => {
            if (!moderator)
                return console.warn(
                    "PreloadModerationService: updateModerator missing moderator",
                );

            let updatedModerator = {};

            try {
                const response = await memeService.updateModerator(
                    moderator.id,
                    premission,
                );

                if (!response.moderator.id)
                    throw new Error("Could not update moderator!");

                updatedModerator = response.moderator;
            } catch (error) {
                notificationService.setMessage(t("moderatorUpdating"));
                console.error(
                    "PreloadModerationService: updateModerator ->",
                    error,
                );
                return false;
            }

            replaceModerator(moderator);
            return updatedModerator;
        },
        [replaceModerator, t],
    );

    const moderatorAddLogic = async (newModerator, premission) => {
        if (!newModerator)
            return console.warn(
                "PreloadModerationService: moderatorAddLogic missing input",
            );

        let addedModerator = {};

        try {
            const response = await memeService.createModerator(
                newModerator,
                premission,
            );

            if (!response.moderator.id) throw new Error("NO MODERATOR ID");

            if (response.errorCode === "MODERATOR_ALREADY_EXISTS") {
                notificationService.setMessage(t("moderatorAlreadyAdded"));
            }

            addedModerator = response.moderator;
        } catch (error) {
            if (addedModerator) {
                if (addedModerator.errorCode === "USER_NAME_NOT_FOUND") {
                    notificationService.setMessage(t("moderatorDoesNotExist"));
                }
            } else {
                notificationService.setMessage(t("moderatorAddingWentWrong"));
                console.error(
                    "PreloadModerationService: moderatorAddLogic ->",
                    error,
                );
            }

            return false;
        }

        return addedModerator;
    };

    const pushModerator = useCallback(
        async (moderator, premission = true) => {
            const addedModerator = await moderatorAddLogic(
                moderator,
                premission,
            );

            if (addedModerator) addEntityState(addedModerator, "moderators");

            return addedModerator;
        },
        [addEntityState, t],
    );

    const addFirstModerator = useCallback(
        async (moderator, premission = true) => {
            const addedModerator = await moderatorAddLogic(
                moderator,
                premission,
            );

            if (addedModerator)
                addFirstEntityState(addedModerator, "moderators");

            return addedModerator;
        },
        [addFirstEntityState],
    );

    const deleteModerator = useCallback(
        async (moderator) => {
            if (!moderator || !moderator.id)
                return console.warn(
                    "PreloadModerationService: deleteModerator missing moderator",
                );

            try {
                const response = await memeService.deleteModerator(
                    moderator.id,
                );
                if (!response.ok) throw new Error("Response was not ok");
            } catch (error) {
                notificationService.setMessage(
                    t("moderatorWhenDeletingWentWrong"),
                );
                console.error(
                    "PreloadModerationService: deleteModerator ->",
                    error,
                );
                return false;
            }

            deleteEntityState(moderator, "moderators");
            return true;
        },
        [deleteEntityState, t],
    );

    const loadModerators = useCallback(async () => {
        return await loadAll(
            memeService.listAllBroadcasterModerators.bind(memeService),
            setModerator,
            "moderators",
        );
    }, [setModerator]);

    const broadcasterModerators = useMemo(() => {
        return getEntities("broadcasterModerators");
    }, [getEntities]);

    const setBroadcasterModerators = useCallback(
        (moderator) => {
            if (!moderator)
                return console.warn(
                    "PreloadModerationService: setModerator missing moderator",
                );

            // Do not use this to update 1 template
            setEntityState(moderator, "broadcasterModerators");
        },
        [setEntityState],
    );

    const loadBroadcasterModerators = useCallback(async () => {
        //Channels the streamer moderates  listAllModeratorChannels
        return await loadAll(
            memeService.listAllModeratorChannels.bind(memeService),
            setBroadcasterModerators,
            "broadcasters",
        );
    }, [setBroadcasterModerators]);

    const loadAll = async (
        serviceFunction,
        setModeratorState,
        responseObjectName,
        disablePagination,
    ) => {
        let moderatorAccumulationMap = [];
        let paginationToken = null;

        try {
            // Start loading the templates
            do {
                const responseObject = await serviceFunction(paginationToken);

                const responseObjectSelector = responseObjectName
                    ? responseObject[responseObjectName]
                    : responseObject.moderators;

                // Accumulate templates
                moderatorAccumulationMap = [
                    ...moderatorAccumulationMap,
                    ...responseObjectSelector,
                ];

                // Update the state once with all the templates
                setModeratorState(moderatorAccumulationMap);

                // Set the next pagination token if exists
                paginationToken = disablePagination
                    ? null
                    : responseObject.paginationToken;
            } while (paginationToken);
        } catch (error) {
            // In case of an error, notify the user and set the empty state
            notificationService.setMessage(t("moderatorCouldNotLoad"));
            console.error(
                "PreloadModerationService: loadAll could not load moderators !",
                error,
            );
            setModeratorState(moderatorAccumulationMap);
        }

        return moderatorAccumulationMap;
    };

    return {
        loadBroadcasterModerators,
        broadcasterModerators,
        loadModerators,
        moderators,
        deleteModerator,
        addFirstModerator,
        pushModerator,
        updateModerator,
        replaceModerator,
    };
};

export default PreloadModerationService;
